import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Upload as UploadIcon, Link as LinkIcon, Loader2 } from "lucide-react";
import { useToast } from "../ui/use-toast";
import { describeImage } from "../../services/ideogramService";

interface DescribeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerateWithPrompt: (prompt: string) => void;
}

export const DescribeDialog: React.FC<DescribeDialogProps> = ({
  open,
  onOpenChange,
  onGenerateWithPrompt,
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'upload' | 'url'>('upload');
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImageUrl("");
      setImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setImageUrl(url);
    setImagePreviewUrl(url);
    setImageFile(null);
  };

  const handleDescribe = async () => {
    try {
      setIsLoading(true);
      const result = await describeImage(imageFile, imageUrl || null);
      setDescription(result);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to describe image",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyDescription = async () => {
    try {
      await navigator.clipboard.writeText(description);
      toast({
        title: "Success",
        description: "Description copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy description",
        variant: "destructive",
      });
    }
  };

  const handleGenerate = () => {
    onGenerateWithPrompt(description);
    onOpenChange(false);
  };

  const resetState = () => {
    if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImageUrl("");
    setImagePreviewUrl("");
    setDescription("");
    setActiveTab('upload');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetState();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Describe Image</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Image Preview */}
          <div className="min-h-[300px] flex items-center justify-center border rounded-lg">
            {imagePreviewUrl ? (
              <img
                src={imagePreviewUrl}
                alt="Preview"
                className="max-w-full max-h-[300px] object-contain rounded-lg"
              />
            ) : (
              <div className="text-center text-gray-400">
                <p>No image selected</p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-1 mb-4">
              <div className="flex">
                <button
                  className={`flex items-center justify-center gap-2 flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'upload'
                      ? 'bg-white text-black shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => setActiveTab('upload')}
                >
                  <UploadIcon className="h-4 w-4" />
                  Upload
                </button>
                <button
                  className={`flex items-center justify-center gap-2 flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'url'
                      ? 'bg-white text-black shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => setActiveTab('url')}
                >
                  <LinkIcon className="h-4 w-4" />
                  URL
                </button>
              </div>
            </div>

            {activeTab === 'upload' ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center text-center">
                <UploadIcon className="h-8 w-8 text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">Drag and drop an image here, or click to select</p>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="image-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('image-upload')?.click()}
                  className="bg-white"
                >
                  Choose File
                </Button>
                {imageFile && (
                  <p className="mt-2 text-sm text-slate-500">{imageFile.name}</p>
                )}
              </div>
            ) : (
              <div>
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={imageUrl}
                  onChange={handleUrlChange}
                  className="w-full"
                />
              </div>
            )}

            {description && (
              <div className="space-y-4">
                <div className="rounded-md bg-slate-100 p-4">
                  <p className="text-sm">{description}</p>
                </div>
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopyDescription}
                    className="text-slate-600 hover:text-slate-900 border-slate-200 hover:bg-slate-50"
                  >
                    Copy Description
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleGenerate}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Generate Image →
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center sm:justify-between">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="text-slate-600 hover:text-slate-900 border-slate-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDescribe}
            disabled={isLoading || (!imageFile && !imageUrl)}
            className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-200"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Describing...
              </>
            ) : (
              'Describe'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
